import { seconds } from "$root/utils/time";
import type { ItemData, DataResponseDTO, DistributionResponseDTO } from "@eu.mdotm.api/smart-workspaces";
import { extractDataProviderValue } from "../WorkspaceDataDefinitionCast";
import { breakdownOthersLabel, type DistributionBinningEchoDTO } from "../spec-gaps";

/**
 * Mappers from the wire shapes to what the new chart components draw.
 *
 * Kept out of the components (and out of `index.tsx`) because they are the only part of the new charts
 * that is worth unit-testing: stable category ordering, the `"Other"` bucket, the half-open bin
 * convention, the density normalization and the per-uuid `totals` / `excluded` split all have to
 * survive real data.
 *
 * Both mappers key the two series *positionally* — `series[0]` = CURRENT, `series[1]` = PROPOSAL — which
 * is the convention the whole workspace chart path already uses.
 */

/** `[CURRENT, PROPOSAL]`. `null` means "this portfolio has no value for that source". */
export type SourcePair<T> = [T, T | null];

/**
 * Binds a request's two uuids to a reader that splits any uuid-keyed map on a distribution response
 * into a {@link SourcePair}: the CURRENT entry, or `fallback` when the request carries no CURRENT uuid
 * or the map has no entry under it; and the PROPOSAL entry, or `null` when the request carries no
 * PROPOSAL uuid at all — the `null` the binned charts read as "there is no second series".
 */
function perUuidOf({ currentUuid, proposalUuid }: { currentUuid?: string; proposalUuid?: string }) {
	return <T>(map: Record<string, T> | undefined, fallback: T): SourcePair<T> => [
		(currentUuid ? map?.[currentUuid] : undefined) ?? fallback,
		proposalUuid ? (map?.[proposalUuid] ?? fallback) : null,
	];
}

// --- Stacked column ------------------------------------------------------------------------------

export type StackedChartItem = {
	id: string;
	name: string;
	/** Value per breakdown category, indexed the same way as {@link StackedChartData.categories}. */
	values: SourcePair<Array<number | null>>;
};

export type StackedChartData = {
	/**
	 * Stable, deduped breakdown categories. Drives legend order *and* colour assignment, so it must not
	 * be derived per row.
	 */
	categories: Array<string>;
	items: Array<StackedChartItem>;
	/** `false` when no portfolio in the response has a proposal value — the second stack is then dropped. */
	hasProposal: boolean;
};

/**
 * Puts the aggregated tail last. The backend emits it as a normal category, but it is not one: it is
 * "everything else", and a legend that sorts it between GBP and JPY reads as a currency.
 */
function withOthersLast(categories: Array<string>): Array<string> {
	const others = categories.filter((category) => category === breakdownOthersLabel);
	return others.length === 0 ? categories : [...categories.filter((c) => c !== breakdownOthersLabel), ...others];
}

export function dataProviderItemsToStackedChartData(
	response: DataResponseDTO,
	series: { currentUuid?: string; proposalUuid?: string },
): StackedChartData {
	const { currentUuid, proposalUuid } = series;

	// Response-level categories first: they are ordered and deduped by the backend, and shared by every
	// row. Unioning row labels is only a fallback for a response that predates `breakdowns`.
	const declared = [
		...(currentUuid ? (response.breakdowns?.[currentUuid]?.categories ?? []) : []),
		...(proposalUuid ? (response.breakdowns?.[proposalUuid]?.categories ?? []) : []),
	];

	const perRow: Array<{ id: string; name: string; values: SourcePair<Map<string, number>> }> = [];
	const observed: Array<string> = [];

	for (const item of response.items ?? []) {
		const id = item.predefinedFields?.uuid;
		if (!id) {
			continue;
		}

		const read = (uuid: string | undefined): Map<string, number> | null => {
			if (!uuid) {
				return null;
			}
			const cell = item.dictionary?.[uuid];
			if (!cell) {
				return null;
			}
			const entries = extractDataProviderValue(cell, "NUMERIC", { breakdown: true }).value?.entries;
			if (!entries) {
				return null;
			}
			const byLabel = new Map<string, number>();
			for (const entry of entries) {
				byLabel.set(entry.label, entry.value);
				if (!observed.includes(entry.label)) {
					observed.push(entry.label);
				}
			}
			return byLabel;
		};

		const current = read(currentUuid);
		const proposal = read(proposalUuid);
		perRow.push({
			id,
			name: item.predefinedFields?.name ?? "missing name",
			values: [current ?? new Map(), proposal],
		});
	}

	const categories = withOthersLast([...new Set(declared.length > 0 ? declared : observed)]);

	return {
		categories,
		hasProposal: perRow.some((row) => row.values[1] != null),
		items: perRow.map(({ id, name, values: [current, proposal] }) => ({
			id,
			name,
			values: [
				categories.map((category) => current.get(category) ?? null),
				proposal ? categories.map((category) => proposal.get(category) ?? null) : null,
			],
		})),
	};
}

/**
 * The "order by the whole stack" option's value. The empty string, because no breakdown label can be one
 * — a sentinel spelled like a label (`"Total"`) would collide with a custom-tag category of that name.
 * {@link StackedChartSort} models the same choice as `category: undefined`.
 */
export const stackTotalSortValue = "";

export type StackedChartSort = {
	/** A label from {@link StackedChartData.categories}. `undefined` orders by the stack total. */
	category?: string;
	/** `undefined` leaves the response order alone. */
	direction?: "ASC" | "DESC";
	/** Which of the two stacks decides the order: `1` (PROPOSAL) while the proposal is highlighted. */
	source?: 0 | 1;
};

/**
 * Orders the portfolios of a stacked column by one stacked segment, or by the stack total.
 *
 * Frontend-side, unlike the column chart's sort, because there is nothing to ask the backend for:
 * `SortCriterionDTO` carries a parameterized-concept uuid and a direction, with no way to name a category,
 * and a breakdown concept declares `sortable = false` anyway. So this reorders the page that was fetched
 * — which also means the request does not change, and switching the sort must not refetch.
 *
 * Rows with no value for the chosen segment always land last, whichever direction was asked for: they are
 * "no data", not "the smallest". `Array#sort` is stable, so they — and any ties — keep the response order.
 */
export function sortStackedChartData(data: StackedChartData, sort: StackedChartSort | undefined): StackedChartData {
	if (!sort?.direction) {
		return data;
	}

	const categoryIndex = sort.category == null ? null : data.categories.indexOf(sort.category);
	if (categoryIndex === -1) {
		// The persisted category is not in this response — the breakdown, the filter or the data moved under
		// it. Leaving the response order alone beats ordering by something the user did not pick.
		return data;
	}

	const keyOf = (item: StackedChartItem): number | null => {
		const values = item.values[sort.source ?? 0] ?? item.values[0];
		if (categoryIndex == null) {
			return values.reduce<number | null>((total, value) => (value == null ? total : (total ?? 0) + value), null);
		}
		return values[categoryIndex] ?? null;
	};

	const factor = sort.direction === "ASC" ? 1 : -1;
	return {
		...data,
		items: [...data.items].sort((a, b) => {
			const [keyA, keyB] = [keyOf(a), keyOf(b)];
			if (keyA == null || keyB == null) {
				return keyA == null ? (keyB == null ? 0 : 1) : -1;
			}
			return (keyA - keyB) * factor;
		}),
	};
}

// --- Frequency -----------------------------------------------------------------------------------

export type FrequencyBin = {
	from: number;
	to: number;
	/** Axis / tooltip label. Half-open `[from, to)`, except the last bin which is inclusive `[from, to]`. */
	label: string;
	counts: SourcePair<number>;
	itemUuids: SourcePair<Array<string>>;
};

export type FrequencyData = {
	bins: Array<FrequencyBin>;
	/**
	 * What the backend actually applied. Never render the requested `binCount` / `binWidth` back as
	 * fact: they are hints, and the backend clamps and rounds even under `COUNT` / `WIDTH`.
	 */
	binning: DistributionBinningEchoDTO;
	/** Items that landed in a bin, per source. The two series do not share the same n. */
	totals: SourcePair<number>;
	/** Items that matched the predicate but had no value, per source. They land in no bin. */
	excluded: SourcePair<number>;
	/** `false` when no portfolio in the response has a proposal value — see {@link binnedHasProposal}. */
	hasProposal: boolean;
};

/**
 * `true` when the proposal series has something to draw: a PROPOSAL uuid in the request *and* at least
 * one portfolio that landed in a bin under it.
 *
 * Read off `totals`, not off the uuid, because the two answer different questions. A chart configured
 * with a PROPOSAL series still gets `counts[1] === 0` in every bin when no portfolio in the predicate has
 * a proposal — nothing to plot, nothing to highlight — and `totals[1]` is exactly the count of the ones
 * that did. The same rule as `StackedChartData.hasProposal` and `LineChartData.hasProposal`, so all four
 * kinds decide "is there a proposal" the same way.
 */
function binnedHasProposal(totals: SourcePair<number>): boolean {
	return (totals[1] ?? 0) > 0;
}

export function binLabel(
	bin: { from: number; to: number },
	isLast: boolean,
	formatValue: (value: number) => string,
): string {
	return `[${formatValue(bin.from)}, ${formatValue(bin.to)}${isLast ? "]" : ")"}`;
}

export function distributionResponseToFrequencyChartData(
	response: DistributionResponseDTO,
	series: { currentUuid?: string; proposalUuid?: string },
	formatValue: (value: number) => string,
): FrequencyData {
	const perUuid = perUuidOf(series);

	const totals = perUuid(response.totals, 0);

	return {
		binning: response.binning,
		totals,
		excluded: perUuid(response.excluded, 0),
		hasProposal: binnedHasProposal(totals),
		bins: response.bins.map((bin, index) => ({
			from: bin.from,
			to: bin.to,
			label: binLabel(bin, index === response.bins.length - 1, formatValue),
			counts: perUuid(bin.counts, 0),
			itemUuids: perUuid(bin.itemUuids, [] as Array<string>),
		})),
	};
}

// --- Density -------------------------------------------------------------------------------------

/**
 * The density chart reads the *same* `POST /data-providers/distribution` response as the frequency
 * chart — same request, same shared bin edges — and turns it into a probability density:
 * `count / total / binWidth`, which integrates to 1 and so is comparable between two series of
 * different n. That last part is the whole reason the Y axis is a density and not a count: with an
 * overlay, CURRENT and PROPOSAL rarely share an n (a portfolio without a proposal has no PROPOSAL
 * value), and two count curves of different heights invite reading a sample-size difference as a
 * distribution difference.
 *
 * The X axis carries the metric's own values, not z-scores. Each series is standardized by its *own*
 * mean and σ, and per-series z-scores cannot share an axis — z = 1 is a different actual value for
 * each series, so plotting both against one z axis would draw a comparison that is not there. Real
 * values keep one honest axis; the z reading is then per series, which is what `stats` carries and
 * what the mean lines draw.
 */

/** Mean, σ and n for one series. `standardDeviation` is 0 (never null) for fewer than two values. */
export type DensitySeriesStats = { mean: number; standardDeviation: number; count: number };

export type DensityBin = {
	from: number;
	to: number;
	/** Bin midpoint — where the density point is plotted, since the density describes the whole bin. */
	center: number;
	/**
	 * The **midpoint**, formatted — not the `[from, to)` range the frequency chart labels its
	 * bins with. Two reasons: the midpoint is literally where the point sits and what the z-score is
	 * computed from, so the tooltip names the thing it is describing; and a range reads badly in a
	 * tooltip header ("[18.94%, 25.25%)") for a curve the eye reads as continuous.
	 */
	label: string;
	/** `count / total / binWidth`, per source. Integrates to 1, so two series of different n compare. */
	density: SourcePair<number>;
	/** Kept alongside the density: a tooltip that only says "0.18" tells nobody how many portfolios that is. */
	counts: SourcePair<number>;
};

export type DensityChartData = {
	bins: Array<DensityBin>;
	/** What the backend actually applied. `binWidth` is the density's divisor, so it is load-bearing here. */
	binning: DistributionBinningEchoDTO;
	totals: SourcePair<number>;
	excluded: SourcePair<number>;
	/** Per source. Drives the mean lines, the normal reference curves and the per-series z in the tooltip. */
	stats: SourcePair<DensitySeriesStats>;
	/** `false` when no portfolio in the response has a proposal value — see {@link binnedHasProposal}. */
	hasProposal: boolean;
};

const emptyStats: DensitySeriesStats = { mean: 0, standardDeviation: 0, count: 0 };

/** The normal PDF, `φ((x − μ) / σ) / σ` — a true density, so it is on the same scale as the bins'. */
export function normalDensityAt(x: number, stats: DensitySeriesStats): number {
	if (stats.standardDeviation <= 0) {
		return 0;
	}
	const z = (x - stats.mean) / stats.standardDeviation;
	return Math.exp(-0.5 * z * z) / (stats.standardDeviation * Math.sqrt(2 * Math.PI));
}

/**
 * The fitted-normal reference curve, sampled evenly across the binned range.
 *
 * Sampled on its own fine grid rather than at the bin centres: the point of drawing it is to compare
 * the empirical shape against a symmetric bell, and a bell drawn through a dozen bin centres has the
 * empirical curve's own kinks. Empty when σ is 0 — there is no curve to fit through one value.
 */
export function normalCurvePoints(
	stats: DensitySeriesStats,
	range: { from: number; to: number },
	sampleCount = 121,
): Array<[number, number]> {
	if (stats.standardDeviation <= 0 || sampleCount < 2 || range.to <= range.from) {
		return [];
	}
	const step = (range.to - range.from) / (sampleCount - 1);
	return Array.from({ length: sampleCount }, (_, index): [number, number] => {
		const x = range.from + index * step;
		return [x, normalDensityAt(x, stats)];
	});
}

export function distributionResponseToDensityChartData(
	response: DistributionResponseDTO,
	series: { currentUuid?: string; proposalUuid?: string },
	formatValue: (value: number) => string,
): DensityChartData {
	const perUuid = perUuidOf(series);

	const totals = perUuid(response.totals, 0);
	// The applied width, never the requested one: it is the divisor that makes the areas integrate to 1,
	// and the backend clamps and rounds it under every mode.
	const binWidth = response.binning.binWidth;
	const densityOf = (count: number, total: number): number =>
		total > 0 && binWidth > 0 ? count / total / binWidth : 0;

	return {
		binning: response.binning,
		totals,
		excluded: perUuid(response.excluded, 0),
		stats: perUuid(response.statistics, emptyStats),
		hasProposal: binnedHasProposal(totals),
		bins: response.bins.map((bin) => {
			const counts = perUuid(bin.counts, 0);
			const center = (bin.from + bin.to) / 2;
			return {
				from: bin.from,
				to: bin.to,
				center,
				label: formatValue(center),
				counts,
				density: [
					densityOf(counts[0], totals[0]),
					counts[1] == null || totals[1] == null ? null : densityOf(counts[1], totals[1]),
				],
			};
		}),
	};
}

// --- Line (time series) --------------------------------------------------------------------------

/** A point as Highcharts wants it: `[timestampMs, value]`. The wire says seconds; this is the conversion. */
export type LineChartPoint = [timestampMs: number, value: number];

export type LineChartItem = {
	id: string;
	name: string;
	/** `[CURRENT, PROPOSAL]`. `null` means the portfolio has no series for that source at all. */
	series: SourcePair<Array<LineChartPoint> | null>;
};

export type LineChartData = {
	/** One entry per portfolio that has at least one point in at least one source. */
	items: Array<LineChartItem>;
	/** `false` when no portfolio in the response has a proposal series — the dashed lines are then dropped. */
	hasProposal: boolean;
	/**
	 * Portfolios that matched the predicate but have no series, per source — a draft portfolio, a missing
	 * proposal, or a portfolio whose window contains no instant.
	 */
	excluded: SourcePair<number>;
};

/**
 * The backend already trims to the requested `timeHorizon` and sorts chronologically, so this only
 * changes units and drops what cannot be plotted: a point is kept when both numbers are finite. A
 * series that ends up empty is `null` — "no line", counted in `excluded` — rather than an empty line
 * that would claim a legend entry and draw nothing.
 */
export function dataProviderItemsToLineChartData(
	response: DataResponseDTO,
	series: { currentUuid?: string; proposalUuid?: string },
): LineChartData {
	const { currentUuid, proposalUuid } = series;

	const read = (item: ItemData, uuid?: string): Array<LineChartPoint> | null => {
		if (!uuid) {
			return null;
		}
		const cell = item.dictionary?.[uuid];
		if (!cell) {
			return null;
		}
		const value = extractDataProviderValue(cell, "TIME_SERIES").value;
		if (!Array.isArray(value)) {
			return null;
		}
		const points = value.flatMap(
			([timestampSec, point]): Array<LineChartPoint> =>
				Number.isFinite(timestampSec) && Number.isFinite(point) ? [[seconds(timestampSec), point]] : [],
		);
		return points.length > 0 ? points : null;
	};

	const items: Array<LineChartItem> = [];
	const excluded: [number, number] = [0, 0];

	for (const item of response.items ?? []) {
		const id = item.predefinedFields?.uuid;
		if (!id) {
			continue;
		}
		const current = read(item, currentUuid);
		const proposal = read(item, proposalUuid);
		if (current == null) {
			excluded[0] += 1;
		}
		if (proposal == null) {
			excluded[1] += 1;
		}
		if (current == null && proposal == null) {
			continue;
		}
		items.push({ id, name: item.predefinedFields?.name ?? "missing name", series: [current, proposal] });
	}

	return {
		items,
		hasProposal: items.some((item) => item.series[1] != null),
		excluded: [excluded[0], proposalUuid ? excluded[1] : null],
	};
}
